import React, { useState, useEffect } from 'react';
import {
  FolderTree, ChevronRight, ChevronDown, Package,
  Tag, Grid, List, Search
} from 'lucide-react';
import './TRMStyles.css';

const TaxonomyBrowser = () => {
  const [taxonomyTree, setTaxonomyTree] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [products, setProducts] = useState([]);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [viewMode, setViewMode] = useState('tree'); // 'tree' or 'grid'
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTaxonomyData();
  }, []);

  const fetchTaxonomyData = async () => {
    try {
      setLoading(true);
      const [treeRes, categoriesRes] = await Promise.all([
        fetch('/api/trm/taxonomy/tree'),
        fetch('/api/trm/taxonomy/categories')
      ]);

      const tree = await treeRes.json();
      const cats = await categoriesRes.json();

      setTaxonomyTree(tree);
      setCategories(Array.isArray(cats) ? cats : []);
    } catch (error) {
      console.error('Error fetching taxonomy:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNodeProducts = async (taxonomyId) => {
    try {
      const response = await fetch(`/api/trm/taxonomy/${taxonomyId}/products`);
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
      setProducts([]);
    }
  };

  const toggleNode = (nodeId) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const selectNode = (node) => {
    setSelectedNode(node);
    fetchNodeProducts(node.taxonomy_id);
  };

  const renderTreeNode = (node, level = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes.has(node.taxonomy_id);
    const isSelected = selectedNode?.taxonomy_id === node.taxonomy_id;

    return (
      <div key={node.taxonomy_id} className="tree-node">
        <div 
          className={`tree-node-content level-${level} ${isSelected ? 'selected' : ''}`}
          onClick={() => selectNode(node)}
          style={{ paddingLeft: `${level * 20 + 10}px` }}
        >
          {hasChildren && (
            <span 
              className="tree-toggle"
              onClick={(e) => {
                e.stopPropagation();
                toggleNode(node.taxonomy_id);
              }}
            >
              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </span>
          )}
          {!hasChildren && <span className="tree-spacer"></span>}
          
          <FolderTree size={16} className="node-icon" />
          <span className="node-name">{node.name}</span>
          <span className="node-code">{node.code}</span>
          
          {node.feaf_category && (
            <span className="node-category">{node.feaf_category}</span>
          )}
        </div>

        {hasChildren && isExpanded && (
          <div className="tree-children">
            {node.children.map(child => renderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  const filterTree = (nodes, term) => {
    if (!term) return nodes;
    
    return nodes.reduce((acc, node) => {
      const matchesSearch = 
        node.name.toLowerCase().includes(term.toLowerCase()) ||
        node.code.toLowerCase().includes(term.toLowerCase()) ||
        (node.description && node.description.toLowerCase().includes(term.toLowerCase()));

      const filteredChildren = node.children ? filterTree(node.children, term) : [];

      if (matchesSearch || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children: filteredChildren
        });
      }

      return acc;
    }, []);
  };

  const filteredTree = filterTree(taxonomyTree, searchTerm);

  if (loading) {
    return (
      <div className="trm-loading">
        <div className="spinner"></div>
        <p>Loading FEAF taxonomy...</p>
      </div>
    );
  }

  return (
    <div className="trm-taxonomy-browser">
      <div className="dashboard-header">
        <h2>📊 FEAF Taxonomy Browser</h2>
        <p>Federal Enterprise Architecture Framework - Software Classification</p>
      </div>

      {/* Toolbar */}
      <div className="taxonomy-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search taxonomy nodes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="view-toggle">
          <button
            className={viewMode === 'tree' ? 'active' : ''}
            onClick={() => setViewMode('tree')}
          >
            <List size={18} /> Tree View
          </button>
          <button
            className={viewMode === 'grid' ? 'active' : ''}
            onClick={() => setViewMode('grid')}
          >
            <Grid size={18} /> Grid View
          </button>
        </div>
      </div>

      <div className="taxonomy-container">
        {/* Left Panel: Taxonomy Tree */}
        <div className="taxonomy-tree-panel">
          <div className="panel-header">
            <h3><FolderTree size={18} /> FEAF Hierarchy</h3>
            <span className="node-count">{taxonomyTree.length} domains</span>
          </div>

          {filteredTree.length > 0 ? (
            <div className="tree-container">
              {filteredTree.map(node => renderTreeNode(node))}
            </div>
          ) : (
            <div className="no-data">
              <FolderTree size={48} />
              <p>No taxonomy nodes found</p>
            </div>
          )}
        </div>

        {/* Right Panel: Selected Node Details & Products */}
        <div className="taxonomy-details-panel">
          {selectedNode ? (
            <>
              <div className="node-details">
                <h3>{selectedNode.name}</h3>
                <div className="node-meta">
                  <span className="code-badge">{selectedNode.code}</span>
                  <span className="level-badge">Level {selectedNode.level}</span>
                  {selectedNode.feaf_category && (
                    <span className="category-badge">{selectedNode.feaf_category}</span>
                  )}
                </div>
                {selectedNode.description && (
                  <p className="node-description">{selectedNode.description}</p>
                )}
              </div>

              <div className="node-products">
                <h4><Package size={18} /> Mapped Products ({products.length})</h4>

                {products.length > 0 ? (
                  <div className="products-grid">
                    {products.map((product, idx) => (
                      <div key={idx} className="product-card">
                        <div className="product-header">
                          <strong>{product.product_name}</strong>
                          {product.is_primary && <span className="primary-badge">Primary</span>}
                          {product.is_verified && <Tag size={14} className="verified-icon" />}
                        </div>
                        <div className="product-meta">
                          <span className="manufacturer">{product.manufacturer_name}</span>
                          {product.confidence && (
                            <span className="confidence">
                              Confidence: {(product.confidence * 100).toFixed(0)}%
                            </span>
                          )}
                        </div>
                        <div className="product-source">
                          <small>Source: {product.mapping_source}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-products">
                    <Package size={32} />
                    <p>No products mapped to this taxonomy node</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="no-selection">
              <FolderTree size={64} />
              <h3>Select a Taxonomy Node</h3>
              <p>Click on a node in the tree to view details and mapped products</p>
            </div>
          )}
        </div>
      </div>

      {/* Categories Summary */}
      {categories.length > 0 && (
        <div className="categories-summary">
          <h3>FEAF Categories</h3>
          <div className="categories-grid">
            {categories.map((cat, idx) => (
              <div key={idx} className="category-chip">
                <Tag size={14} /> {cat}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxonomyBrowser;

